import { useCallback, useState } from 'react';

import debounce from 'lodash/debounce';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useSearchParams } from 'react-router-dom';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useCurrentUser } from '@/services/auth';
import { useSites } from '@/services/sites';
import { formatURLDate } from '@/utils';

import {
  hardcodedSites,
  TRANS_OVA_GENETICS_COMPANY_ID,
} from '../utils/constants';

export const FreezeSelector = ({
  quickSearchValue,
  setQuickSearchValue,
}: {
  quickSearchValue: string;
  setQuickSearchValue: (value: string) => void;
}) => {
  const [params, setParams] = useSearchParams();
  const fertilizationDate = params.get('date');
  const fertilizationSiteId = params.get('sId');

  const user = useCurrentUser();
  const company = user.data?.companies.find((c) => c.isActive);
  const isTransOvaGeneticsGroup =
    company?.companyId === TRANS_OVA_GENETICS_COMPANY_ID;

  const { data: siteData } = useSites({ filter: 'fertilization' });
  const sites = [
    ...(siteData?.map((item) => ({
      label: item.name,
      value: item.id,
    })) || []),
    ...(isTransOvaGeneticsGroup ? hardcodedSites : []),
  ].sort((a, b) => a.label.localeCompare(b.label));

  const handleDateChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set('date', formatURLDate(e.value));
      } else {
        newParams.delete('date');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const handleSiteChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);

      if (e.value) {
        newParams.set('sId', e.value);
      } else {
        newParams.delete('sId');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const [search, setSearch] = useState(quickSearchValue);
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setQuickSearchValue(value);
    }, 1000),
    [setQuickSearchValue],
  );

  return (
    <>
      <div className="freeze-selector">
        <Calendar
          value={fertilizationDate ? new Date(fertilizationDate) : undefined}
          placeholder="Fertilization Date"
          dateFormat="mm-dd-yy"
          onChange={handleDateChange}
          showIcon
          className="w-auto min-w-44"
        />
        <InputText
          placeholder="Search"
          disabled={!fertilizationDate}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            debouncedSetSearch(e.target.value);
          }}
        />
        <Dropdown
          value={fertilizationSiteId}
          options={sites}
          placeholder="Fertilization Site"
          onChange={handleSiteChange}
          disabled={!fertilizationDate || !!search.length}
        />
      </div>
    </>
  );
};
